import React, { Suspense } from 'react';
import {
  Navigate,
  Route,
  Routes as RouterRoutes,
  useLocation,
} from 'react-router';
import FloatingActionBtn from 'src/components/FloatingActionBtn/FloatingActionBtn';
import Footer from 'src/components/Layout/Footer/Footer';
import Header from 'src/components/Layout/Header/Header';
import Loader from 'src/components/Loader/Loader';
import RequireAuth from 'src/components/RequireAuth';
import MasterContainer from 'src/containers/ItemDetail/MasterContainer';
import { bugSnagInititalize } from 'src/helper/bugSnagHelper';
import {
  getAddPaymentUser,
  isGuestUser,
  removeTextToPayUserSessionDetail,
} from 'src/helper/helperMethods';
import { useAppDispatch, useAppSelector } from 'src/redux/store/store';

import RouteWithReactHelmet from '../../components/ReactHelmetHOC/ReactHelmetWrapper';
import * as ROUTE_CONSTANT from '../../constants';
import TextToPayHeader from 'src/components/Layout/Header/TextToPayHeader';
import AddPayment from 'src/Features/order/addPayment';
import LinkExpired from 'src/containers/fallBacks/LinkExpired';
import {
  removeAddPaymentUserData,
  resetCheckoutForTextToPayFlow,
} from 'src/redux/slices/addPayment/addPaymentSlice';
import RequestPayment from 'src/Features/order/requestPayment';
import * as CONSTANT from '../../constants';
import { CampaignTriggerProvider } from 'src/context/CampaignTriggerContext';
import RequiresLocation from 'src/components/RequiresLocation';
import MoreDevices from 'src/Features/profileDetail/AuthenticatedDevices/MoreDevices';
const ErrorBoundaryBugSnag = bugSnagInititalize();

const Login = React.lazy(
  () => import('../../Features/AccountSetup/components/Login'),
);
const HomeContainer = React.lazy(() => import('../../components/Home/Home'));

const OurKitchen = React.lazy(() => import('../../components/Home/OurKItchen'));
const Fundraising = React.lazy(
  () => import('../../components/Home/Fundrasing/Fundrasing'),
);
const Careers = React.lazy(() => import('../../components/Home/Career/Career'));
const CreateAccount = React.lazy(
  () => import('../../Features/AccountSetup/components/CreateAccount'),
);
const Welcome = React.lazy(
  () => import('../../Features/AccountSetup/components/Welcome'),
);
const ForgotPassword = React.lazy(
  () => import('../../Features/AccountSetup/components/ForgotPassword'),
);

const NewPassword = React.lazy(
  () => import('../../Features/AccountSetup/components/NewPassword'),
);
const Menu = React.lazy(() => import('../../Features/Menu/Menu'));
const Loyalty = React.lazy(() => import('../../Features/Rewards//Rewards'));
const noSearchResult = React.lazy(
  () => import('../../components/SearchResult/NoResult'),
);
const otherManuItems = React.lazy(
  () => import('src/containers/OtherManuItems'),
);
const boxLunchesContainer = React.lazy(
  () => import('src/containers/ItemDetail/BoxLunchesContainer'),
);

const ProfileDetail = React.lazy(
  () => import('src/Features/profileDetail/ProfileDetail'),
);

const CaterWithUs = React.lazy(
  () => import('src/components/CaterWithUs/CaterWithUs'),
);

const ContactUs = React.lazy(() => import('src/Features/ContactUs/ContactUs'));
const privacyPolicy = React.lazy(
  () => import('src/Features/PrivacyPolicy/PrivacyPolicy'),
);
const terms = React.lazy(() => import('src/Features/Terms/Terms'));

const RewardsFaqs = React.lazy(
  () => import('src/components/Home/CateringRewardsPage/CateringRewardsPage'),
);
const ViewMoreRecentOrders = React.lazy(
  () =>
    import('src/Features/profileDetail/RecentOrders/ViewMoreRecentOrders/ViewMoreRecentOrders'),
);
const ViewMoreFutureOrders = React.lazy(
  () =>
    import('src/Features/profileDetail/FutureOrders/MoreFutureOrders/MoreFutureOrders'),
);

const AccountDetails = React.lazy(
  () =>
    import('src/Features/profileDetail/HouseAccounts/AccountDetails/AccountDetails'),
);

const CreditDetails = React.lazy(
  () =>
    import('src/Features/profileDetail/HouseAccounts/AccountDetails/CreditDetails'),
);

const RecentOrders = React.lazy(
  () =>
    import('src/Features/profileDetail/HouseAccounts/AccountDetails/RecentOrders'),
);
const GroupOrderDeetails = React.lazy(
  () => import('src/Features/order/groupOrderDetails'),
);
const HouseAccountUserListing = React.lazy(
  () =>
    import('src/Features/profileDetail/HouseAccounts/HouseAccountUsers/HouseAccountUserListing'),
);

const OrderReciept = React.lazy(
  () => import('src/Features/profileDetail/orderReciept/orderReciept'),
);

const ThankYou = React.lazy(
  () => import('src/components/OrderTracking/ThankYou'),
);

const PublicOrderTrackingContainer = React.lazy(
  () => import('src/publicOrderTracking/publicOrderTracking'),
);

const Checkout = React.lazy(() => import('src/Features/order/checkout'));
// const GroupOrderCheckout = React.lazy(
//   () => import('src/Features/Ordering/Checkout/GroupOrderCheckout'),
// );

const GroupOrder = React.lazy(
  () => import('src/Features/GroupOrder/components/GroupOrder'),
);

const Page404 = React.lazy(() => import('src/Features/Page404/page404'));
const invalidateOtp = React.lazy(
  () => import('../../containers/otp/invalidateOtp'),
);

const Location = React.lazy(() => import('src/Features/Location/Location'));
const Routes: React.FunctionComponent = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const excludePgaes = ['/location', '/checkout'];
  const excludePgaesForAddPayment = [
    ROUTE_CONSTANT.ADD_PAYMENT_THANK_YOU,
    ROUTE_CONSTANT.ADD_PAYMENT_LINK_EXPIRED,
    ROUTE_CONSTANT.REQUEST_PAYMENT_LINK_EXPIRED,
    ROUTE_CONSTANT.REQUEST_PAYMENT_THANK_YOU,
  ];
  const addPayment =
    location.pathname.includes(ROUTE_CONSTANT.ROUTE_ADD_PAYMENT) ||
    location.pathname.includes(ROUTE_CONSTANT.ROUTE_REQUEST_PAYMENT);

  const addPaymentThankYouAndExpireLink = !excludePgaesForAddPayment.includes(
    location.pathname,
  );
  const showFooter = !excludePgaes.includes(location.pathname);
  const hideFab =
    location.pathname === ROUTE_CONSTANT.ROUTE_CHECKOUT ||
    location.pathname === ROUTE_CONSTANT.ROUTE_THANK_YOU ||
    addPayment;
  const { user } = useAppSelector((state) => state.user);
  const addPaymentUser = useAppSelector(
    (state) => state.addPayment.addPaymentUser,
  );
  const addPaymentUserSession = getAddPaymentUser();

  if (!addPayment && addPaymentUser?.id) {
    dispatch(removeAddPaymentUserData());
    dispatch(resetCheckoutForTextToPayFlow());
  }

  if (!addPayment && addPaymentUserSession?.customer_id) {
    removeTextToPayUserSessionDetail();
    sessionStorage.removeItem(CONSTANT.ERROR_MESSAGE);
  }

  return (
    <Suspense fallback={<Loader />}>
      {addPayment ? <TextToPayHeader /> : <Header />}
      <ErrorBoundaryBugSnag FallbackComponent={Page404}>
        <RouterRoutes>
          <Route
            path={ROUTE_CONSTANT.ROUTE_HOME}
            // path={ROUTE_CONSTANT.ROUTE_WELCOME_SCREEN}
            element={<RouteWithReactHelmet routeComponent={HomeContainer} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_CATER_WITH_US}
            element={<RouteWithReactHelmet routeComponent={CaterWithUs} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_OUR_KITCHEN}
            element={<RouteWithReactHelmet routeComponent={OurKitchen} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_FUNDRAISING}
            element={<RouteWithReactHelmet routeComponent={Fundraising} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_CAREERS}
            element={<RouteWithReactHelmet routeComponent={Careers} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_LOGIN}
            element={<RouteWithReactHelmet routeComponent={Login} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_CREATE_ACCOUNT}
            element={<RouteWithReactHelmet routeComponent={CreateAccount} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_WELCOME_SCREEN}
            element={<RouteWithReactHelmet routeComponent={Welcome} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_FORGOT_PASSWORD}
            element={<RouteWithReactHelmet routeComponent={ForgotPassword} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_NEW_PASSWORD}
            element={<RouteWithReactHelmet routeComponent={NewPassword} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_MENU}
            element={<RouteWithReactHelmet routeComponent={Menu} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_LOYALTY}
            element={<RequireAuth component={Loyalty} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.ROUTE_MENU}/:selected_category?`}
            element={<RouteWithReactHelmet routeComponent={Menu} />}
          />

          <Route
            path={ROUTE_CONSTANT.ROUTE_PROFILE_DETAILS}
            element={<RequireAuth component={ProfileDetail} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.ROUTE_GROUP_ORDER_DETAILS}/:id`}
            element={<RequireAuth component={GroupOrderDeetails} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_TALK}
            element={<RouteWithReactHelmet routeComponent={ContactUs} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_PRIVACY_POLICY}
            element={<RouteWithReactHelmet routeComponent={privacyPolicy} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_TERMS}
            element={<RouteWithReactHelmet routeComponent={terms} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_PROFILE_RECENT_ORDERS}
            element={
              <RouteWithReactHelmet routeComponent={ViewMoreRecentOrders} />
            }
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_PROFILE_FUTURE_ORDERS}
            element={
              <RouteWithReactHelmet routeComponent={ViewMoreFutureOrders} />
            }
          />
          <Route
            path={`${ROUTE_CONSTANT.PRINT_RECEIPT}/:id`}
            element={<RouteWithReactHelmet routeComponent={OrderReciept} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_NO_SEARCH_RESULT}
            element={<RouteWithReactHelmet routeComponent={noSearchResult} />}
          />

          <Route
            path={`${ROUTE_CONSTANT.ROUTE_OTHER_ITEM}/:id`}
            element={<RequiresLocation component={MasterContainer} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_MAP_LOCATION}
            element={<RouteWithReactHelmet routeComponent={Location} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.ROUTE_LOCATION_COPY}/info/:id`}
            element={<RouteWithReactHelmet routeComponent={Location} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.HOUSE_ACCOUNT_DETAIL}/:id`}
            element={<RequireAuth component={AccountDetails} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.CREDIT_DETAILS}/:id`}
            element={<RequireAuth component={CreditDetails} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.HOUSE_ACCOUNT_RECENT_ORDERS}/:id`}
            element={<RequireAuth component={RecentOrders} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.HOUSE_ACCOUNT_RECENT_ORDERS}`}
            element={<RequireAuth component={RecentOrders} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.ROUTE_PROFILE_DEVICES}`}
            element={<RequireAuth component={MoreDevices} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.HOUSE_ACCOUNT_USER_LISTING}/:id`}
            element={<RequireAuth component={HouseAccountUserListing} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_THANK_YOU}
            element={<RequireAuth component={ThankYou} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.ROUTE_ORDER_TRACKING}/:id`}
            element={
              <RouteWithReactHelmet
                routeComponent={PublicOrderTrackingContainer}
              />
            }
          />
          <Route
            path={`${ROUTE_CONSTANT.ADD_PAYMENT_LINK_EXPIRED}`}
            element={<RouteWithReactHelmet routeComponent={LinkExpired} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.ADD_PAYMENT_THANK_YOU}`}
            element={<RouteWithReactHelmet routeComponent={ThankYou} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.ROUTE_ADD_PAYMENT}/:id`}
            element={<RouteWithReactHelmet routeComponent={AddPayment} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.REQUEST_PAYMENT_LINK_EXPIRED}`}
            element={<RouteWithReactHelmet routeComponent={LinkExpired} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.REQUEST_PAYMENT_THANK_YOU}`}
            element={<RouteWithReactHelmet routeComponent={ThankYou} />}
          />
          <Route
            path={`${ROUTE_CONSTANT.ROUTE_REQUEST_PAYMENT}/:id`}
            element={<RouteWithReactHelmet routeComponent={RequestPayment} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_CHECKOUT}
            element={<RequireAuth component={Checkout} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_GROUP_ORDER}
            element={<RouteWithReactHelmet routeComponent={GroupOrder} />}
          />
          <Route
            path={ROUTE_CONSTANT.LOYALTY_REWARDS_FAQS}
            element={<RouteWithReactHelmet routeComponent={RewardsFaqs} />}
          />
          <Route
            path={CONSTANT.ROUTE_INVALIDATE_OTP}
            element={<RouteWithReactHelmet routeComponent={invalidateOtp} />}
          />
          <Route
            path={ROUTE_CONSTANT.ROUTE_404}
            element={<RouteWithReactHelmet routeComponent={Page404} />}
          />
          <Route
            path="*"
            element={
              <Navigate to={ROUTE_CONSTANT.ROUTE_MENU} replace={true} />
            }
          />
        </RouterRoutes>
        {showFooter || (!addPayment && !addPaymentThankYouAndExpireLink) ? (
          <Footer />
        ) : null}

        {!isGuestUser(user) && !hideFab && <FloatingActionBtn />}
      </ErrorBoundaryBugSnag>
    </Suspense>
  );
};

export default Routes;
